import React, { useState, useEffect } from 'react';
import { 
  FlaskConical, Lightbulb, TreeDeciduous, Building, Sparkles, Send,
  Rocket, Users, Zap, Heart, Star, CheckCircle, ArrowRight, Phone,
  Globe, Award, Target, MessageSquare
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const LaboratorioInnovacionPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [ideaForm, setIdeaForm] = useState({
    name: '',
    email: '',
    phone: '',
    idea: '',
    category: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const inspirationProjects = [
    {
      icon: TreeDeciduous,
      title: 'Árboles Solares',
      description: 'Estructuras artísticas que combinan diseño con generación de energía solar para espacios públicos y privados.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800',
      tags: ['Arte', 'Energía', 'Urbano']
    },
    {
      icon: Building,
      title: 'Mobiliario Urbano Solar',
      description: 'Bancos, paradas de bus y estaciones de carga alimentados por energía solar para ciudades inteligentes.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
      tags: ['Ciudades', 'Sustentable', 'Smart City']
    },
    {
      icon: Globe,
      title: 'Proyectos Comunitarios',
      description: 'Iniciativas solares para escuelas, hospitales y comunidades rurales que necesitan energía limpia.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800',
      tags: ['Social', 'Comunidad', 'Educación']
    },
    {
      icon: Sparkles,
      title: 'Instalaciones Artísticas',
      description: 'Creaciones únicas que combinan arte, luz y energía solar para eventos, exhibiciones y espacios especiales.',
      image: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=800',
      tags: ['Arte', 'Eventos', 'Diseño']
    }
  ];

  const ideaCategories = [
    { value: 'arte_solar', label: 'Arte y Diseño Solar' },
    { value: 'urbano', label: 'Proyecto Urbano' },
    { value: 'comunitario', label: 'Proyecto Comunitario' },
    { value: 'personal', label: 'Proyecto Personal' },
    { value: 'negocio', label: 'Idea de Negocio' },
    { value: 'otro', label: 'Otro' }
  ];

  const handleSubmitIdea = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Send to WhatsApp with all the details
    const message = `🚀 *NUEVA IDEA - LABORATORIO DE INNOVACIÓN*

👤 *Nombre:* ${ideaForm.name}
📧 *Email:* ${ideaForm.email}
📱 *Teléfono:* ${ideaForm.phone}
📂 *Categoría:* ${ideaCategories.find(c => c.value === ideaForm.category)?.label || ideaForm.category}

💡 *LA IDEA:*
${ideaForm.idea}

---
¡Hagamos realidad esta idea juntos!`;
    
    window.open(`https://wa.me/595991344956?text=${encodeURIComponent(message)}`, '_blank');
    
    setSubmitting(false);
    setSubmitted(true);
    
    // Reset after showing success
    setTimeout(() => {
      setSubmitted(false);
      setIdeaForm({ name: '', email: '', phone: '', idea: '', category: '' });
    }, 5000);
  };

  const isFormValid = ideaForm.name && ideaForm.phone && ideaForm.idea && ideaForm.category;

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section - Super Innovador */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden" data-testid="lab-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-purple-900/30 to-zinc-900"></div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#90EE90] rounded-full filter blur-[120px] opacity-20 animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full filter blur-[150px] opacity-10 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-cyan-400 rounded-full filter blur-[100px] opacity-10 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(#90EE90 1px, transparent 1px), linear-gradient(90deg, #90EE90 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#90EE90]/20 to-purple-500/20 rounded-full mb-6 border border-[#90EE90]/30">
              <FlaskConical className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-[#90EE90]">Laboratorio de Innovación</span>
              <Sparkles className="w-4 h-4 text-purple-400" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Tu Idea, Nuestra
              <span className="bg-gradient-to-r from-[#90EE90] to-cyan-400 bg-clip-text text-transparent"> Misión</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-4 leading-relaxed">
              En Zenex no tenemos límites. Si podés imaginarlo, podemos hacerlo realidad.
            </p>
            <p className="text-lg sm:text-xl md:text-2xl font-bold text-[#90EE90] mb-8">
              Cueste lo que cueste.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#enviar-idea"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#90EE90] to-cyan-400 hover:from-[#7DD87D] hover:to-cyan-300 text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#90EE90]/25"
              >
                <Lightbulb size={20} />
                Enviar mi Idea
              </a>
              <a
                href="#inspiracion"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
              >
                Ver Inspiración
                <ArrowRight size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-full mb-8">
              <Heart className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-white">Nuestro Manifiesto</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-8 leading-tight">
              "Creemos que las mejores ideas nacen de personas como vos"
            </h2>
            
            <div className="grid sm:grid-cols-3 gap-6 mb-12">
              <div className="bg-white p-6 rounded-2xl border border-zinc-200">
                <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mx-auto mb-4">
                  <Rocket className="w-6 h-6 text-[#14532D]" />
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">Sin Límites</h3>
                <p className="text-sm text-zinc-600">No existe idea demasiado grande ni demasiado loca.</p>
              </div>
              
              <div className="bg-white p-6 rounded-2xl border border-zinc-200">
                <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mx-auto mb-4">
                  <Users className="w-6 h-6 text-[#14532D]" />
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">Vos Sos el Protagonista</h3>
                <p className="text-sm text-zinc-600">Tu nombre quedará en cada proyecto que creemos juntos.</p>
              </div>
              
              <div className="bg-white p-6 rounded-2xl border border-zinc-200">
                <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mx-auto mb-4">
                  <Target className="w-6 h-6 text-[#14532D]" />
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">Hacemos que Pase</h3>
                <p className="text-sm text-zinc-600">Nuestro equipo técnico y creativo está listo para ejecutar.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inspiration Gallery */}
      <section id="inspiracion" className="py-16 md:py-24" data-testid="inspiration-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Inspiración
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Proyectos que Podemos Crear Juntos
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Estas son solo algunas ideas. Tu imaginación es el único límite.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {inspirationProjects.map((project, index) => {
              const Icon = project.icon;
              return (
                <div 
                  key={index}
                  className="group relative bg-white rounded-2xl overflow-hidden border border-zinc-200 hover:shadow-2xl transition-all duration-500"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-lg font-bold text-white mb-1">{project.title}</h3>
                      <div className="flex flex-wrap gap-1">
                        {project.tags.map((tag, idx) => (
                          <span key={idx} className="text-[10px] bg-[#90EE90]/80 text-zinc-900 px-2 py-0.5 rounded-full font-medium">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <p className="text-sm text-zinc-600">{project.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Submit Idea Form */}
      <section id="enviar-idea" className="py-16 md:py-24 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900" data-testid="idea-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
                <MessageSquare className="w-4 h-4 text-[#90EE90]" />
                <span className="text-sm font-medium text-[#90EE90]">Tu Momento</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
                Contanos tu Idea y la Hacemos Realidad
              </h2>
              
              <p className="text-zinc-400 mb-8 leading-relaxed">
                No importa si es un árbol solar para tu jardín, una instalación artística para tu empresa, 
                o un proyecto comunitario para tu barrio. En Zenex, cada idea tiene valor y merece ser escuchada.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#90EE90] mt-0.5 flex-shrink-0" />
                  <p className="text-zinc-300 text-sm">Evaluamos cada propuesta personalmente</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#90EE90] mt-0.5 flex-shrink-0" />
                  <p className="text-zinc-300 text-sm">Te contactamos en menos de 48 horas</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#90EE90] mt-0.5 flex-shrink-0" />
                  <p className="text-zinc-300 text-sm">Trabajamos juntos en el diseño y ejecución</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#90EE90] mt-0.5 flex-shrink-0" />
                  <p className="text-zinc-300 text-sm">Tu nombre queda asociado al proyecto</p>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-[#90EE90]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-[#14532D]" />
                  </div>
                  <h3 className="text-2xl font-bold text-zinc-900 mb-2">¡Idea Enviada!</h3>
                  <p className="text-zinc-600">
                    Te contactaremos pronto para hacerla realidad.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmitIdea}>
                  <h3 className="text-xl font-bold text-zinc-900 mb-6">Enviá tu Idea</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        value={ideaForm.name}
                        onChange={(e) => setIdeaForm({ ...ideaForm, name: e.target.value })}
                        className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:border-[#90EE90] focus:outline-none transition-colors"
                        placeholder="Tu nombre"
                        required
                      />
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={ideaForm.email}
                          onChange={(e) => setIdeaForm({ ...ideaForm, email: e.target.value })}
                          className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:border-[#90EE90] focus:outline-none transition-colors"
                          placeholder="tu@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">
                          WhatsApp *
                        </label>
                        <input
                          type="tel"
                          value={ideaForm.phone}
                          onChange={(e) => setIdeaForm({ ...ideaForm, phone: e.target.value })}
                          className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:border-[#90EE90] focus:outline-none transition-colors"
                          placeholder="+595 9XX XXX XXX"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1">
                        Categoría *
                      </label>
                      <select
                        value={ideaForm.category}
                        onChange={(e) => setIdeaForm({ ...ideaForm, category: e.target.value })}
                        className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:border-[#90EE90] focus:outline-none transition-colors bg-white"
                        required
                      >
                        <option value="">Seleccionar categoría...</option>
                        {ideaCategories.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1">
                        Describí tu idea *
                      </label>
                      <textarea
                        value={ideaForm.idea}
                        onChange={(e) => setIdeaForm({ ...ideaForm, idea: e.target.value })}
                        rows={5}
                        className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:border-[#90EE90] focus:outline-none transition-colors resize-none"
                        placeholder="Contanos todo sobre tu idea... ¿Qué querés crear? ¿Para qué lo necesitás? ¿Dónde lo instalarías? No te guardes nada, queremos escucharte."
                        required
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={!isFormValid || submitting}
                      className={`w-full py-4 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 ${
                        isFormValid && !submitting
                          ? 'bg-gradient-to-r from-[#90EE90] to-[#7CDF7C] text-zinc-900 hover:shadow-lg'
                          : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                      }`}
                    >
                      {submitting ? (
                        <>Enviando...</>
                      ) : (
                        <>
                          <Send size={18} />
                          Enviar mi Idea
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-zinc-50" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Star className="w-10 h-10 text-[#90EE90] mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-4">
            ¿Preferís contarnos por WhatsApp?
          </h2>
          <p className="text-zinc-600 mb-8">
            También podés escribirnos directamente y charlamos sobre tu idea.
          </p>
          <a
            href="https://wa.me/595991344956?text=Hola%20Zenex!%20Tengo%20una%20idea%20para%20el%20Laboratorio%20de%20Innovación"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-zinc-900 text-white font-bold rounded-xl hover:bg-zinc-800 transition-colors"
          >
            <Phone className="w-5 h-5" />
            Chatear por WhatsApp
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default LaboratorioInnovacionPage;
